import App from "app";
import api from "core/api/api";
import config from "config";
import _ from "underscore";
import axios from "axios";
import { contextStore } from "src/stores";
import licenseService from "core/services/metaStoreService/license-service";
import copilotService from "core/services/copilotService";
import propertyTemplateService from "core/services/metaStoreService/property-template-service";
import { SegmentationFeaturesStates } from "src/modules/user-plan/pages/user-plan/tabs/user-plan/components";
import { segmentationVariants } from "src/modules/common/segmentation/segmentation.consts";
import {
    addonFeaturesKeys,
    ProductTiers,
    AppAdapters,
    AppAdaptersLicenses
} from "src/modules/user-plan/pages/user-plan/tabs/user-plan/components/add-on-features/add-on-features-utils";
import monitorService from "../monitorService";
import Tracker from "core/services/tracker/tracker";
import dictionary from "../../../app/components/common/helpable/online-help-dictionary";
import userPreferences from "../userPreferences/user-preferences";
import trackingCookieExists from "../tracker/trackingCookieExists";
import { TRACKER_STRINGS } from "../tracker/constants";

const cloudLicenses = [
    "AzureSQLServer",
    "AzureHDInsight",
    "AzureStorage",
    "AzureEventHub",
    "AzureDMS",
    "AzureSqlDW",
    "AzureCosmosDB",
    "AzureSqlDwIncrementalSource",
    "GooglePostgresSql",
    "GoogleMySql",
    "GoogleGCS",
    "GoogleBigQuery",
    "AzureCosmosDB",
    "AzurePostgreSQL",
    "AwsRedShift",
    "GoogleSpanner",
    "AzureSqlDW",
    "Snowflake",
    "DeltaLake",
    "SnowflakePartnerConnect",
    "SMSGC"
];

const SAAS_ENVIRONMENTS = [/us-striim.app/, /striim.app/, /stg-az-striimcloudapp.com/, /stg-striimcloudapp.com/];

class SecurityService {
    constructor() {
        this.licensingOptions = null;
        this.segmentationFeaturesStates = null;
        this.currentSegmentationLicense = null;
        this.inlineDocRoot = null;
        this.periodicCheckInterval = null;
        this.periodicCheckTimer = 60 * 1000;
        this.appPermissions = [
            "*:*:dashboard_ui:*",
            "*:*:sourcepreview_ui:*",
            "*:*:apps_ui:*",
            "*:*:admin_ui:*",
            "*:*:monitor_ui:*"
        ];
        this.addonNotAvailableLink = "#/account-and-billing";
        this._isSaasDeployment = undefined;
    }

    async #periodicCheck() {
        if (this.periodicCheckInterval) {
            clearTimeout(this.periodicCheckInterval);
        }

        this.periodicCheckInterval = null;

        try {
            const isLoggedIn = await this.isLoggedIn();
            if (!isLoggedIn) {
                this.logout();
            }
        } catch (e) {
            this.logout();
        }
        // Use setTimeout for recursive scheduling to avoid multiple overlapping intervals getting created
        this.periodicCheckInterval = setTimeout(() => this.#periodicCheck(), this.periodicCheckTimer);
    }

    #sessionEnded(isSaaS = false) {
        if (isSaaS) {
            App.navigate("#/ended");
        } else {
            App.navigate("#/");
        }
        api.disconnect();
        clearTimeout(this.periodicCheckInterval);
        window.location.reload();
    }

    async getToken() {
        if (api.token) {
            return api.token;
        } else {
            try {
                const token = await api.getToken();
                return token;
            } catch (e) {
                throw e;
            }
        }
    }

    start() {
        App.reqres.setHandler("user:permissions", this.fetchPermissions.bind(this));
        App.reqres.setHandler(
            "security:isAdvancedVisualizationsLicense",
            this.isAdvancedVisualizationsLicense.bind(this)
        );
    }

    async isLoggedIn() {
        try {
            const token = await this.getToken();
            const isAuthenticated = await api.isAuthenticated(token);
            if (isAuthenticated) {
                await setToken(token);
                return true;
            } else {
                await setToken(null);
                throw new Error("Not authenticated");
            }
        } catch (e) {
            throw e;
        }
    }

    /*
     * Call from any module's init to determine if the current token is still valid
     */
    async authenticate() {
        try {
            // Run authentication checks in parallel
            const [isLoggedIn, user] = await Promise.all([this.isLoggedIn(), this.getUser()]);

            App.user = user;

            if (isLoggedIn) {
                //Fetch core authentication data in parallel
                const [
                    licensingOptions,
                    _void, // propertyTemplateService.init()
                    _void2, // this.fetchPermissions()
                    currentLicense,
                    documentationRoot,
                    inlineDocRoot,
                    isSaasDeployment
                ] = await Promise.all([
                    api.getCurrentLicensingOption(),
                    propertyTemplateService.init(),
                    this.fetchPermissions(),
                    api.getCurrentLicense(),
                    api.getDocumentationRoot(),
                    api.getInlineDocRoot(),
                    api.isSaasDeployment()
                ]);

                // Store the authoritative isSaaS value from backend
                this._isSaasDeployment = isSaasDeployment;

                // Set the core authentication state
                App.trigger("app:authenticated");
                this.licensingOptions = licensingOptions;
                this.currentSegmentationLicense = currentLicense?.productTier;
                dictionary.updateRootPath(documentationRoot);
                this.inlineDocRoot = inlineDocRoot;

                // Initialize services that can run in parallel
                await Promise.all([
                    monitorService.init(),
                    this.updateSegmentationFeatureStates(),
                    licenseService.init(),
                    copilotService.init()
                ]);

                if (this.isAppAdapterLicense()) {
                    const docVersion = dictionary.getDocVersion().replaceAll(".", "");
                    dictionary.updateRootPath(
                        "https://striim.com/docs/archive/" + docVersion + "/StriimCloudForApplicationIntegration/"
                    );
                }
                this.periodicCheckInterval = setTimeout(() => this.#periodicCheck(), this.periodicCheckTimer);

                if (this.isDeveloperLicense() && trackingCookieExists()) {
                    setTimeout(() => {
                        Tracker.getInstance().track(
                            TRACKER_STRINGS.schema.logIn,
                            {
                                event: "Signed in to Striim Developer",
                                user: user?.displayname
                            },
                            TRACKER_STRINGS.version.withModification
                        );
                    }, 1000);
                }
                return true;
            } else {
                throw new Error("Not authenticated");
            }
        } catch (e) {
            console.error(e);
            throw e;
        }
    }

    async loginDashboard(key) {
        try {
            const { uuidstring: token } = await api.authenticateDashboardEmbedUser(key);
            api.token = token;
            await this.authenticate();
            return;
        } catch (e) {
            console.error("Error during dashboard login:", e);
            throw e;
        }
    }

    async fetchPermissions() {
        if (!App.user) {
            return [];
        }

        if (App.userPermissions) {
            return App.userPermissions;
        }

        try {
            const permissions = await api.getAllowedPermissions(App.user.name, this.appPermissions);

            const isUIReadOnly = config.get("UIReadOnly");
            const permissionValue = isUIReadOnly ? false : true;
            permissions["*:*:dashboard_ui_edit:*"] = permissionValue;
            permissions["*:*:sourcepreview_ui_edit:*"] = permissionValue;
            permissions["*:*:apps_ui_edit:*"] = permissionValue;
            permissions["*:*:admin_ui_edit:*"] = permissionValue;
            permissions["*:*:monitor_ui_edit:*"] = permissionValue;

            App.userPermissions = permissions;

            return permissions;
        } catch (e) {
            console.error(e);
            throw e;
        }
    }

    async login(model) {
        try {
            const formData = "username=" + model.username + "&password=" + encodeURIComponent(model.password);
            const result = await axios.post("/security/authenticate", formData);
            const token = result.data.token;
            await setToken(token);

            const authenticated = await this.authenticate();

            if (authenticated) {
                await userPreferences.load();
                App.trigger("app:authenticated");
                return token;
            } else {
                await setToken(null);
                return null;
            }
        } catch (e) {
            await setToken(null);
            const msg = e.response?.data?.message || e.message;
            throw msg;
        }
    }

    async getUser() {
        try {
            const user = await api.getAuthenticatedUser();
            user.displayname =
                user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.mainEmail || user.name;
            return user;
        } catch (e) {
            console.error(e);
            throw e;
        }
    }

    isStriimDeveloperEnvironment() {
        // From developer.striim.com
        return window.location.host === "developer.striim.com";
    }

    isAdmin = () => {
        const result = App.user?.roles?.some(
            role => role?.domain === "Global" && (role?.roleName === "admin" || role?.roleName === "saasadmin")
        );
        return result;
    };

    isSaaSEnvironment() {
        const isSaaS = App.user?.originType === "SAAS";
        if (isSaaS) {
            return true;
        }

        // Use backend value if available (value based on striim.node.isSaaS)
        if (this._isSaasDeployment !== undefined) {
            return this._isSaasDeployment;
        }

        // Final fallback to hostname-based detection (legacy behavior)
        const host = window.location.host;
        return SAAS_ENVIRONMENTS.some(saasEnv => saasEnv.test(host));
    }

    logout() {
        // Clear authentication state immediately
        const isSaaS = App.user?.originType === "SAAS";

        // Batch clear all authentication-related state
        App.token = null;
        App.userPermissions = undefined;
        api.token = null;

        // Clear storage and context
        sessionStorage.clear();
        contextStore.backboneStore.setPermissionsChecked(false);

        // Perform logout request (non-blocking)
        fetch("/security/logout/", { method: "POST" })
            .then(() => {
                this.#sessionEnded(isSaaS);
            })
            .catch(() => {
                this.#sessionEnded(isSaaS);
            });
    }

    async getCurrentLicensingOption() {
        if (this.licensingOptions) {
            return this.licensingOptions;
        }

        try {
            const licensingOptions = await api.getCurrentLicensingOption();
            this.licensingOptions = licensingOptions;
            return licensingOptions;
        } catch (e) {
            console.error("Failed to get licensing options:", e);
            throw e;
        }
    }

    async isCloudLicense() {
        try {
            const licensingOptions = await this.getCurrentLicensingOption();
            return cloudLicenses.some(license => licensingOptions.includes(license));
        } catch (e) {
            console.error("Failed to check cloud license:", e);
            return false;
        }
    }
    /**
     * Checks if it's a bidirectional server
     */
    async isBidirectional() {
        try {
            const licensingOptions = await this.getCurrentLicensingOption();
            return licensingOptions.includes("Bidirectional");
        } catch (e) {
            console.error("Failed to check bidirectional license:", e);
            return false;
        }
    }

    isAdvancedVisualizationsLicense() {
        const licenses = ["AdvancedVisualizations"];
        return licenses.some(license => this.licensingOptions.includes(license));
    }

    isSnowflakePartnerConnect() {
        const licenses = ["SnowflakePartnerConnect"];
        return licenses.some(license => this.licensingOptions.includes(license));
    }

    async updateSegmentationFeatureStates() {
        try {
            const result = await api.getFeatureStates();
            this.segmentationFeaturesStates = result;
            App.vent.trigger("segmentation:refreshedstates");
            return result;
        } catch (e) {
            console.error("Failed to update segmentation feature states:", e);
            return null;
        }
    }
    getSegmentationVariant(feature) {
        const state = this.getSegmentationFeatureState(feature);

        switch (state) {
            case SegmentationFeaturesStates.ADDON_DISABLED:
                return segmentationVariants.addOn;
            case SegmentationFeaturesStates.ADDON_ENABLED:
                return segmentationVariants.added;
            case SegmentationFeaturesStates.NOTAPPLICABLE:
                return [addonFeaturesKeys.DASHBOARDS, addonFeaturesKeys.WACTION_STORE].indexOf(feature) !== -1
                    ? segmentationVariants.enterprise
                    : segmentationVariants.enterprisePlus;
            case SegmentationFeaturesStates.DEFAULT:
                return segmentationVariants.none;
            default:
                return segmentationVariants.none;
        }
    }

    isSegmentationFeatureEnabled(feature) {
        const state = this.getSegmentationFeatureState(feature);
        return state === SegmentationFeaturesStates.DEFAULT || state === SegmentationFeaturesStates.ADDON_ENABLED;
    }

    getSegmentationFeatureState(feature) {
        if (!this.segmentingEnabled()) {
            return SegmentationFeaturesStates.DEFAULT;
        }

        if (!this.segmentationFeaturesStates || !this.segmentationFeaturesStates[feature]) {
            return SegmentationFeaturesStates.DEFAULT;
        }

        return this.segmentationFeaturesStates[feature];
    }

    segmentingEnabled() {
        return this.currentSegmentationLicense !== ProductTiers.NONSEGMENTED;
    }

    isDeveloperLicense() {
        return this.currentSegmentationLicense === ProductTiers.DEVELOPER;
    }

    isAppAdapterLicense() {
        return Object.values(AppAdapters).includes(this.currentSegmentationLicense);
    }

    isLicenseOptionSQL2FabricSolution() {
        return api.isLicenseOptionSQL2FabricSolution();
    }

    isFabricSolution() {
        const licenses = ["SQL2FabricSolution", "SQL2FabricMirroringSolution"];
        return _.intersection(licenses, this.licensingOptions).length > 0;
    }

    getCurrentAppAdapterLicenseName() {
        switch (this.currentSegmentationLicense) {
            case AppAdapters.APPADAPTERSSTRIPE:
                return AppAdaptersLicenses[AppAdapters.APPADAPTERSSTRIPE];
            case AppAdapters.APPADAPTERSZENDESK:
                return AppAdaptersLicenses[AppAdapters.APPADAPTERSZENDESK];
            case AppAdapters.APPADAPTERSHUBSPOT:
                return AppAdaptersLicenses[AppAdapters.APPADAPTERSHUBSPOT];
            default:
                return "";
        }
    }

    isLicenseOptionDisableAIInsights() {
        return api.isLicenseOptionDisableAIInsights();
    }
}

const setToken = async token => {
    if (token) {
        try {
            api.setToken(token);
            await api.createContext();
        } catch (e) {
            throw e;
        }
    }
};

const securityService = new SecurityService();

export default securityService;
